/**
 * CompetencyGrowthBadge — tiny peripheral coaching-awareness whisper
 * for the Work Mode lesson timer header (Stage-2 P3 enhancement,
 * Feb 18, 2026).
 *
 * Doctrine (locked by operator brief):
 *   • Glance-safe. Read-only. No interaction, no animation, no popup.
 *   • Not a score. Not gamified. No percentages. No pass/fail. No red.
 *   • Hidden entirely when there's no meaningful summary to show.
 *   • Tiny + peripheral — must NOT compete with the lesson timer.
 *   • Reuses `useCompetencyGrowth` (existing infra). No new endpoint.
 *
 * Calm-copy resolver lives in `lib/competencyGrowthCopy.js` so the
 * unit test stays free of axios/ESM transform issues.
 */
import useCompetencyGrowth from "../../hooks/useCompetencyGrowth";
import useDriveModeState from "../../hooks/useDriveModeState";
import { buildCalmCopy, copyMode } from "../../lib/competencyGrowthCopy";


const CTX_KEY = "wm_competency_growth_ctx_v1";

function loadContext() {
  try {
    const raw = localStorage.getItem(CTX_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}


export default function CompetencyGrowthBadge({ lessonId, enabled }) {
  const { movingState } = useDriveModeState();
  const ctx = loadContext();

  // Backwards-compatible gate: Work Mode passes `lessonId` and the
  // badge surfaces inside the live lesson header. Other surfaces
  // (e.g. instructor home) pass `enabled` directly with no lesson
  // context.
  const isEnabled = enabled !== undefined ? Boolean(enabled) : Boolean(lessonId);

  const { summary, prompts } = useCompetencyGrowth({
    focus:       ctx.focus,
    environment: ctx.environment,
    complexity:  ctx.complexity ? Number(ctx.complexity) : undefined,
    stage:       ctx.stage,
    movingState: movingState || "unknown",
    enabled:     isEnabled,
  });

  if (!isEnabled) return null;

  const copy = buildCalmCopy({ summary, prompts, ctx });
  if (!copy) return null;

  return (
    <span
      data-testid="competency-growth-badge"
      data-copy-mode={copyMode(copy)}
      className="block text-[10px] uppercase tracking-wide text-slate-400/80 font-medium mt-0.5"
    >
      {copy}
    </span>
  );
}
